import { yupResolver } from "@hookform/resolvers/yup";
import { CircularProgress } from "@mui/material";
import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import * as yup from "yup";

import Input from "components/Input";
import LinkButton from "components/LinkButton";
import { pwdRecoveryStep1 } from "pages/Login/utils/pwdRecoveryPopUp";
import { useAuth } from "providers/Auth";
import { UserLoginData } from "types/auth";
import { cpfAndCnpjMask } from "utils/formMasks";
import { validateCpfAndCnpj } from "utils/formValidations";

const SubmitBtn = styled.button`
  width: 100%;
  padding: 0.875rem 1.5rem;
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 2rem;
  font-size: 0.9375rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 4px 14px rgba(var(--primary-rgb), 0.35);
  margin-top: 1.5rem;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(var(--primary-rgb), 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(var(--primary-rgb), 0.3);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 3px;
  }
`;

const ForgotRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.25rem;
`;

const LoginForm = () => {
  const navigate = useNavigate();
  const { isLoading, login } = useAuth();

  const schema = yup.object().shape({
    email: yup
      .string()
      .required("*Campo obrigatório")
      .when("isEmail", {
        is: true,
        then: yup.string().email("*Formato incorreto"),
        otherwise: yup
          .string()
          .test("cpfValidator", "*CPF/CNPJ inválido", validateCpfAndCnpj)
          .transform((value) => value.replace(/\D/g, "")),
      }),
    password: yup.string().required("*Campo obrigatório"),
    isEmail: yup.boolean(),
    keepConnected: yup.boolean(),
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<UserLoginData>({ resolver: yupResolver(schema) });

  const onSubmit = async (data: UserLoginData) => {
    login(data, navigate);
  };

  const handleUserInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (inputValue.match("@")) {
      setValue("isEmail", true);
      setValue("email", inputValue);
    }
    if (!inputValue.match(/([a-z])/gi)) {
      setValue("isEmail", false);
      setValue("email", cpfAndCnpjMask(inputValue));
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      aria-label="Formulário de login"
      noValidate
    >
      <div className="mb-3">
        <Input
          label="E-mail, CPF ou CNPJ"
          id="login-email"
          name="email"
          placeholder="seu@email.com ou 000.000.000-00"
          register={register("email")}
          error={errors?.email?.message}
          onChange={handleUserInput}
          type="text"
          autoFocus
          autoComplete="username"
        />
      </div>

      <div className="mb-1">
        <Input
          label="Senha"
          id="login-password"
          name="password"
          placeholder="••••••••"
          isPasswordInput
          register={register("password")}
          error={errors?.password?.message}
          autoComplete="current-password"
        />
      </div>

      <ForgotRow>
        <LinkButton
          onClick={pwdRecoveryStep1}
          disabled={isLoading}
          className="small"
        >
          Esqueci a senha
        </LinkButton>
      </ForgotRow>

      <SubmitBtn type="submit" disabled={isLoading}>
        {isLoading ? (
          <>
            <CircularProgress color="inherit" size={16} />
            Entrando…
          </>
        ) : (
          "Entrar na conta"
        )}
      </SubmitBtn>
    </form>
  );
};

export default LoginForm;
